import {createApiRef, DiscoveryApi, FetchApi} from "@backstage/frontend-plugin-api";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { DashboardInfo } from "@internal/backstage-plugin-dashboard-backend/src/supersetService";

export interface DashboardApi {
  getSupersetDomain(): Promise<string>;
  getAvailableDashboards(): Promise<DashboardInfo[]>
  getGuestToken(): Promise<string>;
  getDashboardEmbeddingUUID(dashboardId: number): Promise<string>;
  executeDashboardEmbedding(mountPoint: HTMLDivElement, domain: string, embeddingUUID: string, guestToken: string): void;
}

export class DashboardClient implements DashboardApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  async getSupersetDomain(): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('dashboard');
    const response = await this.fetchApi.fetch(`${baseUrl}/superset_domain`);

    if (!response.ok) {
      throw new Error(`Failed to fetch superset domain.`);
    }
    const data = await response.json();
    return data.domain;
  }

  async getAvailableDashboards(): Promise<DashboardInfo[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('dashboard');
    const response = await this.fetchApi.fetch(`${baseUrl}/available_dashboards`);

    if (!response.ok) {
      throw new Error(`Failed to fetch available dashboards.`);
    }
    const data = await response.json();
    return data.result;
  }

  async getGuestToken(): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('dashboard');
    const response = await this.fetchApi.fetch(`${baseUrl}/guest_token`);

    if (!response.ok) {
      throw new Error(`Failed to fetch guest token.`);
    }
    const data = await response.json();
    return data.token;
  }

  async getDashboardEmbeddingUUID(dashboardId: number): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('dashboard');
    const response = await this.fetchApi.fetch(`${baseUrl}/embedding_uuid/${dashboardId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch embedding UUID for dashboard ${dashboardId}.`);
    }
    const data = await response.json();
    return data.uuid;
  }

  executeDashboardEmbedding(mountPoint: HTMLDivElement, domain: string, embeddingUUID: string, guestToken: string) {
      embedDashboard({
          debug: true,
          id: embeddingUUID,
          supersetDomain: domain,
          mountPoint: mountPoint, // any html element that can contain an iframe
          fetchGuestToken: async () => guestToken,
          dashboardUiConfig: { // dashboard UI config: hideTitle, hideTab, hideChartControls, filters.visible, filters.expanded (optional), urlParams (optional)
              hideTitle: false,
              hideTab: false,
              hideChartControls: false,
              filters: {
                  expanded: false,
              },
          },
          // optional additional iframe sandbox attributes
          // iframeSandboxExtras: ['allow-top-navigation', 'allow-popups-to-escape-sandbox']
      }).catch((error:any) => { throw new Error(`Failed to embed dashboard: ${error}`); });
  }
}

export const dashboardApiRef = createApiRef<DashboardApi>().with({
  id: 'plugin.dashboard.service',
  pluginId: 'dashboard',
});
